import { z } from 'zod';
import { ApiErrorSchema } from '@web/shared';
import { getToken, clearSession } from './session';

/** Base URL for the API. Empty string in dev → same-origin, proxied by Vite. */
export const API_BASE: string = __API_URL__;

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly requestId?: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

interface RequestOptions<TResponse> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  schema: z.ZodType<TResponse>;
  body?: unknown;
  signal?: AbortSignal;
  /** Set false for the public auth calls that must not send a stale token. */
  auth?: boolean;
}

export async function apiRequest<TResponse>(
  path: string,
  { method = 'GET', schema, body, signal, auth = true }: RequestOptions<TResponse>,
): Promise<TResponse> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  const token = auth ? getToken() : null;
  if (token) headers['authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    signal,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const raw: unknown = await res.json().catch(() => null);

  if (!res.ok) {
    if (res.status === 401 && auth) clearSession();
    const parsed = ApiErrorSchema.safeParse(raw);
    if (parsed.success) {
      const { code, message, requestId, details } = parsed.data.error;
      throw new ApiRequestError(message, res.status, code, requestId, details);
    }
    throw new ApiRequestError(`Request failed (${res.status})`, res.status, 'unknown');
  }

  return schema.parse(raw);
}

/** For endpoints that take a raw binary body (image upload) instead of JSON —
 * `apiRequest` always JSON-encodes, so this is a small sibling rather than an
 * overload. Response is still the usual JSON envelope. */
export async function uploadBinary<TResponse>(
  path: string,
  body: Blob,
  schema: z.ZodType<TResponse>,
  signal?: AbortSignal,
): Promise<TResponse> {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    signal,
    headers: {
      'content-type': body.type || 'application/octet-stream',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body,
  });
  const raw: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    if (res.status === 401) clearSession();
    const parsed = ApiErrorSchema.safeParse(raw);
    if (parsed.success) {
      const { code, message, requestId, details } = parsed.data.error;
      throw new ApiRequestError(message, res.status, code, requestId, details);
    }
    throw new ApiRequestError(`Request failed (${res.status})`, res.status, 'unknown');
  }
  return schema.parse(raw);
}

/** Fetches an authenticated, non-JSON resource (a Whisper/report image) as a
 * `Blob` — a plain `<img src>` can't carry a bearer token, so every
 * authenticated image goes through this + `URL.createObjectURL`. Pass
 * `bustCache` on a user-triggered retry so a previously-failed response isn't
 * re-served from the HTTP cache. */
export async function fetchAuthedBlob(
  path: string,
  opts: { signal?: AbortSignal; bustCache?: boolean } = {},
): Promise<Blob> {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    signal: opts.signal,
    cache: opts.bustCache ? 'reload' : 'default',
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    if (res.status === 401) clearSession();
    throw new ApiRequestError(`Request failed (${res.status})`, res.status, 'unknown');
  }
  const blob = await res.blob();
  // A 200 with a genuinely empty body is a broken object — surface it as a
  // retryable failure rather than a silently-broken <img>. (Content-type is not
  // checked here: the server already normalizes it, and the <img> tag is the
  // decoder of record.)
  if (blob.size === 0) {
    throw new ApiRequestError('Empty image', res.status, 'empty_image');
  }
  return blob;
}
